import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createCreativeSet,
  deleteCreativeSet as deleteCreativeSetRequest,
  fetchCreativeSet,
  updateCreativeSet,
} from 'modules/api/creativeSets';
import {
  transformSetCreateData,
  transformSetDetails,
  transformSetUpdateData,
} from 'components/pages/ContentManagement/transformRequestData';
import { CancelFunctions } from 'components/common/types';
import { ThunkApiConfig } from 'components/common/types/Redux.types';
import { Dsp, Advertiser, Brand } from 'components/common/types/Deal.types';
import { CreativeSetPanel } from 'components/common/types/Creative.types';

import { addToastNotification } from 'store/notification/reducer';
import { changeIsEditingDisabled, fetchUpdateSetDetails } from './reducer';

type SaveCreativeSetDetailsPayload = {
  cancelFunctions: CancelFunctions;
  data: {
    dsp: Dsp | null;
    advertiser: Advertiser | null;
    brand: Brand | null;
    panels: CreativeSetPanel[];
    code: string;
    visualUnitIds: string[];
  };
  id: string;
  onSuccess: Function;
  ordered?: boolean;
};

type GetCreativeSetDetailsPayload = {
  cancelFunctions: CancelFunctions;
  id: string;
};

type DeleteCreativeSetDetailsPayload = {
  cancelFunctions: CancelFunctions;
  onSuccess: Function;
  data: {
    id: string;
    code: string;
  };
};

export const saveSetDetails = createAsyncThunk<void, SaveCreativeSetDetailsPayload, ThunkApiConfig>(
  'creativeSetDetails/saveSetDetails',
  async ({ cancelFunctions, data, id, ordered, onSuccess }, thunkAPI) => {
    const { dispatch } = thunkAPI;

    if (!data.advertiser?.code || !data.brand?.code) {
      dispatch(
        addToastNotification({
          description:
            'We have been unable to save your creative set. Please ensure an Advertiser and Brand is added to all creatives before saving a new creative set.',
          timeout: 10000,
          type: 'Failure',
        }),
      );
      return;
    }

    try {
      dispatch(changeIsEditingDisabled(true));
      const result = id
        ? transformSetDetails(await updateCreativeSet(cancelFunctions, transformSetUpdateData(data), id))
        : transformSetDetails(await createCreativeSet(cancelFunctions, transformSetCreateData(data, ordered)));
      dispatch(
        addToastNotification({
          description: `Your Creative set, ${result.code}, has been saved successfully`,
          timeout: 2000,
          type: 'Success',
        }),
      );
      dispatch(fetchUpdateSetDetails(result));
      if (onSuccess) onSuccess();
    } catch {
      dispatch(changeIsEditingDisabled(false));
    }
  },
);

export const getSetDetails = createAsyncThunk<void, GetCreativeSetDetailsPayload, ThunkApiConfig>(
  'creativeSetDetails/getSetDetails',
  async ({ cancelFunctions, id }, thunkAPI) => {
    const { dispatch } = thunkAPI;

    try {
      dispatch(changeIsEditingDisabled(true));
      const result = transformSetDetails(await fetchCreativeSet(cancelFunctions, id));
      dispatch(fetchUpdateSetDetails(result));
    } catch {
      dispatch(changeIsEditingDisabled(false));
    }
  },
);

export const deleteCreativeSet = createAsyncThunk<void, DeleteCreativeSetDetailsPayload, ThunkApiConfig>(
  'creativeSetDetails/deleteCreativeSet',
  async ({ cancelFunctions, data, onSuccess }, thunkAPI) => {
    const { dispatch } = thunkAPI;

    try {
      const { id, code } = data;
      dispatch(changeIsEditingDisabled(true));
      await deleteCreativeSetRequest(cancelFunctions, id);
      dispatch(changeIsEditingDisabled(false));
      dispatch(
        addToastNotification({
          description: `Your Creative set, ${code}, has been deleted`,
          timeout: 2000,
          type: 'Success',
        }),
      );
      if (onSuccess) onSuccess();
    } catch {
      dispatch(changeIsEditingDisabled(false));
    }
  },
);
